<template>
  <v-row dense>
    <v-col cols="12">
      <v-card class="top-assessores-card" outlined>
        <v-card-title class="py-4 flex-wrap">
          <div>
            <h3 class="font-weight-medium mb-0">{{ assessorTitle }}</h3>
            <p class="caption grey--text">
              Ranking com base na {{ rankingMetricLabel }}. Exibindo 10 por pagina.
            </p>
          </div>
          <v-spacer></v-spacer>
          <div class="d-flex align-center ranking-controls">
            <v-btn-toggle
              v-model="assessorRankingMode"
              dense
              rounded
              class="ranking-toggle"
            >
              <v-btn small value="pontuacao_assessor">Assessor</v-btn>
              <v-btn small value="pontuacao_meta">Meta</v-btn>
            </v-btn-toggle>
            <v-btn icon @click="$emit('refresh')">
              <v-icon>mdi-refresh</v-icon>
            </v-btn>
          </div>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pa-0">
          <template v-if="loading">
            <v-skeleton-loader
              class="px-4 py-4"
              type="list-item-two-line@5"
            ></v-skeleton-loader>
          </template>
          <template v-else>
            <v-list dense>
              <v-list-item
                v-for="assessor in topAssessores"
                :key="assessor.id"
                class="px-4"
              >
                <v-list-item-content>
                  <div class="d-flex align-center justify-space-between mb-1">
                    <div class="d-flex align-center">
                      <span
                        class="rank-chip"
                        :class="'rank-' + assessor.posicao"
                      >
                        {{ assessor.posicao }}
                      </span>
                      <div class="ml-3">
                        <p class="subtitle-2 font-weight-medium mb-0">
                          {{ assessor.nome_completo }}
                        </p>
                        <p class="caption grey--text mb-0">
                          {{ formatNumber(assessor.total_cadastrado) }} cadastros
                        </p>
                      </div>
                    </div>
                    <div class="text-right">
                      <p class="subtitle-2 font-weight-bold success--text mb-0">
                        {{ formatPontuacao(assessor.metricValue) }}
                      </p>
                      <p class="caption grey--text mb-0">
                        {{ rankingMetricLabel }}
                      </p>
                      <v-btn
                        icon
                        small
                        class="mt-1"
                        title="Detalhes"
                        @click="abrirDetalheAssessor(assessor)"
                      >
                        <v-icon small>mdi-account-search</v-icon>
                      </v-btn>
                    </div>
                  </div>
                  <v-progress-linear
                    rounded
                    height="8"
                    :value="rankingProgress(assessor)"
                    :color="progressColor(assessor.posicao)"
                  ></v-progress-linear>
                </v-list-item-content>
              </v-list-item>
              <div v-if="!topAssessores.length" class="py-6 text-center grey--text">
                Sem dados para exibir. Ajuste os filtros acima.
              </div>
            </v-list>
          </template>
        </v-card-text>
        <v-card-actions v-if="assessorTotalPages > 1" class="justify-center">
          <v-pagination
            v-model="assessorPage"
            :length="assessorTotalPages"
            :total-visible="7"
            circle
          ></v-pagination>
        </v-card-actions>
      </v-card>

      <assessor-detalhe-modal
        v-model="detalheDialog"
        :assessor="detalheAssessor"
        :pessoas="detalhePessoas"
        :loading="detalheLoading"
        :search.sync="detalheSearch"
      />
    </v-col>
  </v-row>
</template>

<script>
import AclAPI from "./../../../api/acl";
import { formatNumber, formatPontuacao } from "./utils/formatters";
import AssessorDetalheModal from "./modals/AssessorDetalheModal.vue";

export default {
  name: "TopAssessores",
  components: {
    AssessorDetalheModal,
  },
  props: {
    assessores: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    selectEquipe: {
      type: [String, Number],
      required: true,
    },
    selectMeta: {
      type: [String, Number],
      required: true,
    },
  },
  data() {
    return {
      assessoresOrdenados: [],
      topAssessores: [],
      assessorRankingMode: "pontuacao_assessor",
      maiorPontuacaoAssessor: 0,
      assessorPage: 1,
      assessorPageSize: 10,
      detalheDialog: false,
      detalheLoading: false,
      detalheSearch: "",
      detalheAssessor: {
        id: null,
        nome: "",
      },
      detalhePessoas: [],
    };
  },
  computed: {
    assessorTotalPages() {
      if (!this.assessoresOrdenados.length) {
        return 0;
      }
      return Math.ceil(this.assessoresOrdenados.length / this.assessorPageSize);
    },
    assessorTitle() {
      return this.assessorPage === 1 ? "Top 10 Assessores" : "Ranking de Assessores";
    },
    rankingMetricLabel() {
      return this.assessorRankingMode === "pontuacao_meta"
        ? "pontuação meta"
        : "pontuação assessor";
    },
    rankingMetricKey() {
      return this.assessorRankingMode === "pontuacao_meta"
        ? "pontuacao_meta"
        : "pontuacao_assessor";
    },
  },
  watch: {
    assessorRankingMode() {
      this.assessorPage = 1;
      this.atualizarTopAssessores();
    },
    assessorPage() {
      this.atualizarTopAssessores();
    },
    assessores: {
      handler() {
        this.atualizarTopAssessores();
      },
      immediate: true,
    },
    detalheDialog(valor) {
      if (!valor) {
        this.detalheSearch = "";
        this.detalhePessoas = [];
      }
    },
  },
  methods: {
    formatNumber,
    formatPontuacao,
    atualizarTopAssessores() {
      if (!this.assessores.length) {
        this.topAssessores = [];
        this.assessoresOrdenados = [];
        this.maiorPontuacaoAssessor = 0;
        return;
      }

      const metricKey = this.rankingMetricKey;

      const sorted = [...this.assessores]
        .sort(
          (a, b) =>
            (Number(b[metricKey]) || 0) - (Number(a[metricKey]) || 0)
        )
        .map((assessor, index) => ({
          ...assessor,
          metricValue: Number(assessor[metricKey]) || 0,
          posicao: index + 1,
        }));

      this.assessoresOrdenados = sorted;
      this.maiorPontuacaoAssessor = sorted.length ? sorted[0].metricValue : 0;

      const startIndex = (this.assessorPage - 1) * this.assessorPageSize;
      const endIndex = startIndex + this.assessorPageSize;
      this.topAssessores = sorted.slice(startIndex, endIndex);
    },
    rankingProgress(assessor) {
      if (!this.maiorPontuacaoAssessor) return 0;
      const value = Number(assessor.metricValue) || 0;
      return Math.min((value / this.maiorPontuacaoAssessor) * 100, 100);
    },
    progressColor(posicao) {
      const palette = ["primary", "success", "info", "warning", "pink"];
      const index = Math.max(Number(posicao) - 1, 0);
      return palette[index % palette.length];
    },
    abrirDetalheAssessor(assessor) {
      if (!assessor || !assessor.id) {
        return;
      }

      this.detalheAssessor = {
        id: assessor.id,
        nome: assessor.nome_completo || "Assessor",
      };
      this.detalheDialog = true;
      this.detalheSearch = "";
      this.buscarPessoasAssessor();
    },
    buscarPessoasAssessor() {
      if (!this.detalheAssessor.id) {
        return;
      }

      this.detalheLoading = true;
      this.detalhePessoas = [];

      const headers = this.Helpers.getHeaderRequisicao();
      const params = {
        id_assessor: this.detalheAssessor.id,
      };

      return AclAPI.getPessoas(headers, params)
        .then((response) => {
          const token = response.data.token;
          localStorage.setItem("token", token);

          const pessoas = response.data.pessoas || [];
          this.detalhePessoas = pessoas;
        })
        .catch(() => {
          this.detalhePessoas = [];
          this.Helpers.logout();
        })
        .finally(() => {
          this.detalheLoading = false;
        });
    },
  },
};
</script>

<style scoped>
.top-assessores-card {
  border-radius: 16px;
  min-height: 140px;
}

.rank-chip {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.75rem;
  color: #ffffff;
  background-color: #2196f3;
}

.rank-1 {
  background-color: #f9a825;
}

.rank-2 {
  background-color: #90a4ae;
}

.rank-3 {
  background-color: #fb8c00;
}

.ranking-controls {
  gap: 8px;
}

.ranking-toggle {
  background-color: #eceff1;
}
</style>
